import { useState, useEffect, useContext, useRef } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  MessageCircle, Send, Bot, User, Clock, CheckCheck,
  HelpCircle, BookOpen, Mail
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export const SupportPage = () => {
  const { user, token } = useContext(UserContext);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    // Poll for new messages every 5 seconds
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/support/messages`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await axios.post(`${API}/support/send`, {
        message: newMessage
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage("");
      fetchMessages();
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const quickQuestions = [
    "How do I deposit money?",
    "How do I withdraw winnings?",
    "My bet didn't register",
    "I have a payment issue",
    "How does VIP work?"
  ];

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#007AFF] to-[#00C853] flex items-center justify-center">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-['Rajdhani']">Live Support</h1>
            <p className="text-[#A1A1AA] text-sm">We typically respond within 5 minutes</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chat Window */}
          <div className="lg:col-span-2 flex flex-col h-[600px] rounded-2xl bg-[#1A1B1F] border border-white/10 overflow-hidden">
            {/* Chat Header */}
            <div className="p-4 border-b border-white/10 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#00E701]/20 flex items-center justify-center">
                <Bot className="w-5 h-5 text-[#00E701]" />
              </div>
              <div>
                <p className="text-white font-medium">Support Team</p>
                <p className="text-[#00E701] text-xs flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-[#00E701] animate-pulse" />
                  Online
                </p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <MessageCircle className="w-12 h-12 text-white/10 mx-auto mb-3" />
                  <p className="text-[#A1A1AA]">Start a conversation with our support team</p>
                </div>
              )}

              {messages.map((msg) => (
                <div 
                  key={msg.message_id}
                  className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] ${msg.sender === 'user' ? 'order-1' : ''}`}>
                    <div className={`p-3 rounded-2xl ${
                      msg.sender === 'user' 
                        ? 'bg-[#007AFF] text-white rounded-tr-sm' 
                        : 'bg-white/5 text-white rounded-tl-sm'
                    }`}>
                      <p>{msg.message}</p>
                    </div>
                    <p className={`text-[#52525B] text-xs mt-1 flex items-center gap-1 ${
                      msg.sender === 'user' ? 'justify-end' : ''
                    }`}>
                      <Clock className="w-3 h-3" />
                      {formatTime(msg.created_at)}
                      {msg.sender === 'user' && <CheckCheck className="w-3 h-3 text-[#007AFF]" />}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Questions */}
            {messages.length === 0 && (
              <div className="p-4 border-t border-white/10">
                <p className="text-[#A1A1AA] text-xs mb-2">Quick questions:</p>
                <div className="flex flex-wrap gap-2">
                  {quickQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => setNewMessage(q)}
                      className="px-3 py-1 rounded-full text-xs bg-white/5 text-white/70 hover:bg-white/10 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t border-white/10">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Type your message..."
                  className="flex-1 h-12 bg-black/20 border-white/10 text-white"
                  disabled={sending}
                  data-testid="support-input"
                />
                <Button
                  onClick={sendMessage}
                  disabled={sending || !newMessage.trim()}
                  className="h-12 w-12 bg-[#007AFF] hover:bg-[#0066DD] text-white p-0"
                  data-testid="send-btn"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Help Section */}
          <div className="space-y-4">
            {/* FAQ */}
            <div className="p-4 rounded-xl bg-[#1A1B1F] border border-white/10">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-[#FFD600]" />
                Common Questions
              </h3>
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-white/5">
                  <p className="text-white text-sm font-medium">How do I deposit?</p>
                  <p className="text-[#A1A1AA] text-xs mt-1">
                    Go to Profile → Buy Credits → Select a package
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-white/5">
                  <p className="text-white text-sm font-medium">When do I get my winnings?</p>
                  <p className="text-[#A1A1AA] text-xs mt-1">
                    Winnings are credited instantly to your wallet
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-white/5">
                  <p className="text-white text-sm font-medium">What is rakeback?</p>
                  <p className="text-[#A1A1AA] text-xs mt-1">
                    A percentage of your wagers returned to you based on VIP level
                  </p>
                </div>
              </div>
            </div>

            {/* Resources */}
            <div className="p-4 rounded-xl bg-[#1A1B1F] border border-white/10">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#00E701]" />
                Resources
              </h3>
              <div className="space-y-2">
                <a href="#" className="block p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                  <p className="text-white text-sm">Terms of Service</p>
                </a>
                <a href="#" className="block p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                  <p className="text-white text-sm">Privacy Policy</p>
                </a>
                <a href="#" className="block p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                  <p className="text-white text-sm">Responsible Gaming</p>
                </a>
              </div>
            </div>

            {/* Contact Info */}
            <div className="p-4 rounded-xl bg-gradient-to-br from-[#007AFF]/20 to-transparent border border-[#007AFF]/30">
              <h3 className="text-white font-bold mb-2 flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#007AFF]" />
                Email Support
              </h3>
              <p className="text-[#A1A1AA] text-sm">
                For complex issues, email us at:
              </p>
              <p className="text-[#007AFF] font-mono mt-1">
                support@betindia.com
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
